/**
 * IntercomStore
 *
 * Tracks whether the Intercom messenger SDK is loaded and `window.Intercom`
 * is callable. Set by `<IntercomMessengerLoader />` after the dynamic chunk
 * boots; read by getting-started UI to gate tour-dependent action items
 * and surface a fallback banner when the SDK fails to load.
 *
 * State semantics:
 *   null  — unknown (loader not yet mounted, e.g. feature flag off, or still
 *           probing window.Intercom)
 *   true  — SDK ready, tours can fire
 *   false — loader gave up after the boot timeout, surface unavailable banner
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface IntercomState {
  available: boolean | null;
}

interface IntercomActions {
  setAvailable: (value: boolean | null) => void;
}

type IntercomStore = IntercomState & IntercomActions;

export const useIntercomStore = create<IntercomStore>()(
  devtools(
    (set) => ({
      available: null,
      setAvailable: (value) => set({ available: value }, false, 'setAvailable')
    }),
    {
      name: 'IntercomStore',
      enabled: process.env.NODE_ENV === 'development'
    }
  )
);

/** Non-hook getter for use outside React (e.g. inside the loader effect). */
export const getIntercomActions = () => ({
  setAvailable: useIntercomStore.getState().setAvailable
});
